import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Layout, message, Skeleton } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { PRODUCT } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { ProductCatalogHeader } from '../../organisms/product-catalog/ProductCatalogHeader';
import { FinalTileHeader } from '../../organisms/product-catalog/FinalTileHeader';
import { ProductCatalogTable } from '../../organisms/product-catalog/ProductCatalogTable';
import { FinalTileTable } from '../../organisms/product-catalog/FinalTileTable';
import {
  syncProductCatalogFromEcomRepository,
  getProductCatalogRepository,
} from '../../../repository/mytm/shop-product/shopProductRepository';
import { getTextsRepository, KeyText } from '../../../repository/mytm/text/textRepository';
import { FormProductCatalogModal } from '../../organisms/product-catalog/forms/FormProductCatalogModal';
import { PER_PAGE_20, PER_PAGE_OTHER } from '../../../constants/common';
import { getTileWidgetListRepository } from '../../../repository/mytm/shop/shopRepository';
import { SHOP_TYPE, ShopType } from '../../../types/shop';
import { FormFinalTileModal } from '../../organisms/product-catalog/forms/FormFinalTileModal';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

const { Content } = Layout;

export const ProductCatalog = () => {
  const { t } = useTranslation(PRODUCT);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState<Boolean>(false);
  const [isOpenModalFinal, setIsOpenModalFinal] = useState<Boolean>(false);
  const [dataRecord, setDataRecord] = useState(null);
  const [dataFinalTiles, setDataFinalTiles] = useState([]);
  const [dataFinalTile, setDataFinalTile] = useState(null);
  const [textProduct, setTextProduct] = useState({ titleProduct: '', subTitleProduct: '' });
  const [countrySelected, setCountrySelected] = useCountrySelection(null);
  const [countrySelectedFinal, setCountrySelectedFinal] = useCountrySelection(null);

  const updateProductFromEcom = async () => {
    if (!loading && countrySelected) {
      setLoading(true);
      await syncProductCatalogFromEcomRepository({ country: countrySelected })
        .then(r => {
          mutate();
          setLoading(false);
          message.success(t('message.updateProductEcomSuccess'));
        })
        .catch(e => {
          setLoading(false);
          message.error(e?.errorMessage || t('message.updateProductEcomError'));
        });
    }
  };

  const handleGetText = async (country: string | null = 'USA') => {
    if (!country) return; // Don't fetch if country not selected

    const texts = await getTextsRepository();
    let titleProduct = '';
    let subTitleProduct = '';
    if (texts && texts.length) {
      if (country === 'CAN') {
        titleProduct = texts.find(val => val?.keyText === KeyText.CA_PRODUCT_CATALOG)?.value;
        subTitleProduct = texts.find(val => val?.keyText === KeyText.CA_SUB_PRODUCT_CATALOG)?.value;
      } else {
        titleProduct = texts.find(val => val?.keyText === KeyText.US_PRODUCT_CATALOG)?.value;
        subTitleProduct = texts.find(val => val?.keyText === KeyText.US_SUB_PRODUCT_CATALOG)?.value;
      }
      setTextProduct({
        titleProduct,
        subTitleProduct,
      });
    }
  };

  useEffect(() => {
    handelGetFinalTile();
  }, []);

  useEffect(() => {
    handleGetText(countrySelected);
  }, [countrySelected]);

  useEffect(() => {
    handelGetFinalTile();
  }, [countrySelectedFinal]);

  const errorCount = useRef(0);
  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [countrySelected] : null,
    () => getProductCatalogRepository({ country: countrySelected! }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const handelGetFinalTile = useCallback(async () => {
    if (!countrySelectedFinal) return; // Don't fetch if country not selected

    const swrFinalTile: any = await getTileWidgetListRepository({
      page: 1,
      take: PER_PAGE_OTHER,
      country: countrySelectedFinal,
      type: SHOP_TYPE.FINAL_TILE,
      sort: true,
    });
    if (swrFinalTile.widgets && swrFinalTile.widgets.length) {
      const widgets = swrFinalTile?.widgets.map((val: ShopType, index: number) => {
        return {
          index,
          ...val,
        };
      });
      setDataFinalTiles(widgets);
    } else {
      setDataFinalTiles([]);
    }
  }, [countrySelectedFinal]);

  if (!data || error) {
    return (
      <Container>
        <ProductCatalogHeader textProduct={textProduct} onGetEcomProduct={updateProductFromEcom} />
        <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
        <Content>
          <Skeleton active />
        </Content>
      </Container>
    );
  }

  const recordProductCatalog = (data?.overwrite_products || []).map((item: any, index: number) => ({
    ...item,
    ...{ index },
  }));

  // const recordFinalTile = swrFinalTile?.data

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const onCloseModal = () => {
    setIsOpenModal(false);
  };

  const onCloseModalFinalTile = () => {
    setIsOpenModalFinal(false);
  };

  return (
    <Container>
      <ProductCatalogHeader textProduct={textProduct} onGetEcomProduct={updateProductFromEcom} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      <Content>
        <ProductCatalogTable
          setIsOpenModal={value => setIsOpenModal(value)}
          records={recordProductCatalog}
          setDataRecord={setDataRecord}
          loading={loading || isValidating}
          setLoading={setLoading}
          onRefresh={mutate}
          pagination={{
            pageSize: PER_PAGE_20,
          }}
          countrySelected={countrySelected || 'USA'}
        />
      </Content>
      {/* Final Tile */}
      <FinalTileHeader setIsOpenModal={setIsOpenModalFinal} dataFinalTiles={dataFinalTiles} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelectedFinal(item)} />
      <Content>
        <FinalTileTable
          setIsOpenModal={value => setIsOpenModalFinal(value)}
          records={dataFinalTiles}
          setDataFinalTile={setDataFinalTile}
          loading={loading || isValidating}
          setLoading={setLoading}
          onRefresh={handelGetFinalTile}
          pagination={{
            pageSize: PER_PAGE_20,
          }}
        />
      </Content>
      {isOpenModal && (
        <FormProductCatalogModal
          setDataRecord={setDataRecord}
          onRefresh={mutate}
          record={dataRecord}
          onClose={onCloseModal}
        />
      )}
      {isOpenModalFinal && countrySelectedFinal && (
        <FormFinalTileModal
          setDataRecord={setDataFinalTile}
          onRefresh={handelGetFinalTile}
          record={dataFinalTile}
          onClose={onCloseModalFinalTile}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}
  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }
  .draft-tourtrash-table {
    .ant-table-thead {
      display: none;
    }
  }
  .ant-spin-nested-loading > div > .ant-spin {
    background: rgba(255, 255, 255, 0.7);
    max-height: 100%;
  }
  .ant-pagination-item a {
    line-height: 30px;
  }
`;
